import { Injectable } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { WorkspaceFileType } from '@common/workspace/enums/workspace-file-type.enum';
import { WorkspaceFileUserStatus } from '@common/workspace/enums/workspace-file-user-status.enum';
import { WorkspaceFileUser } from '@common/workspace/models/workspace-file-user.model';
import { WorkspaceFetchResult } from '@common/workspace/models/workspace-fetch-result.model';
import { WorkspaceFile } from '@common/workspace/models/workspace-file.model';
import { WorkspacePullRequestBody } from '@common/workspace/models/workspace-pull-request-body.model';
import { WorkspacePullStateResponse } from '@common/workspace/models/workspace-pull-state-response.model';
import { CollabApiService } from '@portal-core/collab/services/collab-api.service';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { GetDataOptions } from '@portal-core/data/common/models/get-data-options.model';
import { DataService } from '@portal-core/data/common/services/data.service';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { WorkspaceFilesDataService } from '@portal-core/workspaces/services/workspace-files/workspace-files-data.service';
import { uniqBy } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
@Resettable()
export class WorkspaceFilesService extends CollectionServiceBase<WorkspaceFile> {
  /* To help show when the workspace grid finishes loading. */
  public workspaceItemsLoaded: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private collabApiService: CollabApiService,
    private workspaceFilesDataService: WorkspaceFilesDataService,
    protected dataService: DataService
  ) {
    super(workspaceFilesDataService, dataService);
  }

  getWorkspaceFile(projectId: number, branchName: string, filePath: string): WorkspaceFile {
    return this.workspaceFilesDataService.getItemByProperties('projectId', projectId, 'branchName', branchName, 'filePath', filePath);
  }

  getUniqueWorkspaceFileActiveUserIds(workspaceFiles: WorkspaceFile[], invokerUserId: string): string[] {
    return [
      ...new Set(
        workspaceFiles.flatMap(workspaceFile => {
          return workspaceFile.activeUsers
            ?.filter(collabUser => collabUser.userId && collabUser.userId !== invokerUserId)
            ?.map(collabUser => collabUser.userId);
        })
      )
    ];
  }

  getUniqueWorkspaceFileUsers(workspaceFiles: WorkspaceFile[], invokerUserId: string, status?: WorkspaceFileUserStatus): WorkspaceFileUser[] {
    return uniqBy(
      workspaceFiles.flatMap(workspaceFile => {
        return workspaceFile.userStatuses?.filter(workspaceFileUser => {
          return workspaceFileUser.userId && workspaceFileUser.userId !== invokerUserId && (!status || workspaceFileUser.status === status);
        });
      }).filter(workspaceFileUser => !!workspaceFileUser),
      'userId'
    );
  }

  getWorkspaceFile$(projectId: number, branchName: string, filePath: string, options: GetDataOptions = null): Observable<WorkspaceFile> {
    return this.dataService.getData<WorkspaceFile>({
      get: () => this.workspaceFilesDataService.getItemByProperties$('projectId', projectId, 'branchName', branchName, 'filePath', filePath),
      fetch: () => this.collabApiService.getWorkspaceFile$(projectId, branchName, filePath),
      set: collabWorkspaceFileMongoItem => this.workspaceFilesDataService.addItems$([collabWorkspaceFileMongoItem])
    }, options);
  }

  getWorkspaceFilesPage$(filter: PageFilter, projectId: number, branchName: string): Observable<Page<WorkspaceFile>> {
    return this.collabApiService.getWorkspaceFilesPage$(filter, projectId, branchName);
  }

  bulkUpdateWorkspaceFileStatus$(projectId: number, docIds: string[], schemaNames: string[], userId: string, status: WorkspaceFileUserStatus, docVersions?: number[]): Observable<any> {
    return this.collabApiService.bulkUpdateWorkspaceFileStatus$(projectId, docIds, schemaNames, userId, status, docVersions);
  }

  fetchWorkspace$(projectId: number, branchName: string): Observable<MadCloudResult<WorkspaceFetchResult>> {
    return this.collabApiService.fetchWorkspace$(projectId, branchName);
  }

  pullWorkspace$(body: WorkspacePullRequestBody): Observable<MadCloudResult<WorkspacePullStateResponse>> {
    return this.collabApiService.pullWorkspace$(body);
  }

  getWorkspaceFileTypeText(fileType: WorkspaceFileType): string {
    switch (fileType) {
      case WorkspaceFileType.Snippet:
        return 'Snippet'
      case WorkspaceFileType.TemplatePage:
        return 'Template Page';
      case WorkspaceFileType.Topic:
        return 'Topic';
      case WorkspaceFileType.ConditionTagSet:
        return 'Condition Tag Set';
      case WorkspaceFileType.VariableSet:
        return 'Variable Set';
      case WorkspaceFileType.Target:
        return 'Target';
      case WorkspaceFileType.Toc:
        return 'TOC';
      case WorkspaceFileType.Stylesheet:
        return 'Stylesheet';
      case WorkspaceFileType.Destination:
        return 'Destination';
      case WorkspaceFileType.Project:
        return 'Project';
      case WorkspaceFileType.Code:
        return 'Code';
      default:
        return 'Unknown';
    }
  }
}
